import {
  Button,
  List,
  ListItemButton,
  ListItemText,
  Skeleton,
  TextField,
  Typography,
} from '@mui/material';
import { grey } from '@mui/material/colors';
import {
  MdCancel,
  MdOutlineChevronLeft,
  MdOutlineChevronRight,
} from '@teamworksdev/icons';
import { useState } from 'react';
import { useTranslation } from 'react-i18next';
import { trpc } from '../../../trpc/react';
import { Drawer, DrawerFooter, DrawerHeader } from '../../components/drawer';
import { useToast } from '../../hooks/useToast';
import { CreateOrgInput, EditOrgInput, Org } from './types';

const PAGE_LIMIT = 100;

type Cursor = {
  previousPage: string;
  nextPage: string;
};

export function AddEditOrgSheet({
  open,
  onEdit,
  onCreate,
  onClose,
  selectedOrg,
  existingOrgAxleIds,
}: {
  open: boolean;
  onEdit: (input: EditOrgInput) => void;
  onCreate: (input: CreateOrgInput) => void;
  onClose: () => void;
  selectedOrg?: Org;
  existingOrgAxleIds: string[];
}) {
  const { t } = useTranslation();
  const [step, setStep] = useState<'org' | 'teams' | 'profiles'>(
    selectedOrg ? 'teams' : 'org',
  );
  const [axleOrgId, setAxleOrgId] = useState<string | null>(
    selectedOrg ? selectedOrg.axleOrgId : null,
  );
  const [axleTeamIds, setAxleTeamIds] = useState<string[]>(
    selectedOrg
      ? selectedOrg.teams
          .map((team) => team.axleTeamId)
          .filter((t) => !t.startsWith('sc-coaches-team-axle-id-'))
      : [],
  );
  const [axleCoachProfileIds, setAxleCoachProfileIds] = useState<string[]>(
    selectedOrg
      ? selectedOrg.coachAxleProfiles.map((profile) => profile.axleProfileId)
      : [],
  );
  const { toast } = useToast();
  const action = selectedOrg ? t('editOrg') : t('createOrg');

  const onNextStep = () => {
    if (step === 'org') {
      setStep('teams');
    } else if (step === 'teams') {
      setStep('profiles');
    }
  };
  const onPrevStep = () => {
    if (step === 'profiles') {
      setStep('teams');
    } else if (step === 'teams') {
      setStep('org');
    }
  };

  const onSave = () => {
    // Coaches can be deselected down to zero when editing (removal is the
    // point of this step in edit mode); a new org still needs at least one.
    if (
      !axleOrgId ||
      !axleTeamIds.length ||
      (!axleCoachProfileIds.length && !selectedOrg)
    ) {
      toast({
        title: t('invalidOrgInput'),
        severity: 'error',
      });
      return;
    } else {
      if (selectedOrg) {
        // edit
        onEdit({
          axleOrgId: selectedOrg.axleOrgId,
          axleTeamIds,
          axleCoachProfileIds,
          orgId: selectedOrg.id,
        });
      } else {
        // create
        onCreate({
          axleOrgId,
          axleTeamIds,
          axleCoachProfileIds,
        });
      }
    }
  };
  return (
    <Drawer open={open} onClose={onClose}>
      <DrawerHeader title={`${action}`} onClose={onClose}>
        <Typography variant="body2" className="mt-1 text-gray-500">
          {t('stepCounter', {
            current: step === 'org' ? 1 : step === 'teams' ? 2 : 3,
            total: 3,
          })}
        </Typography>
      </DrawerHeader>

      <div className="overflow-auto px-6">
        {step === 'org' ? (
          <OrgStep
            existingOrgAxleIds={existingOrgAxleIds}
            onSelectOrg={(axleOrgId) => {
              setAxleOrgId(axleOrgId);
              setStep('teams');
            }}
          />
        ) : step === 'teams' ? (
          <TeamsStep
            editOrg={selectedOrg}
            axleOrgId={axleOrgId ?? ''}
            selectedTeamIds={axleTeamIds}
            onAddTeam={(team) => {
              setAxleTeamIds([...axleTeamIds, team]);
            }}
            onRemoveTeam={(axleTeamId) =>
              setAxleTeamIds(axleTeamIds.filter((id) => id !== axleTeamId))
            }
          />
        ) : (
          <ProfilesStep
            axleOrgId={axleOrgId ?? ''}
            axleTeamIds={axleTeamIds}
            selectedProfileIds={axleCoachProfileIds}
            onAddProfile={(profile) =>
              setAxleCoachProfileIds([...axleCoachProfileIds, profile])
            }
            onRemoveProfile={(axleId) =>
              setAxleCoachProfileIds(
                axleCoachProfileIds.filter((id) => id !== axleId),
              )
            }
          />
        )}
      </div>
      <div className="flex grow" />
      <DrawerFooter className="p-6 flex justify-between">
        <Button variant="outlined" onClick={onClose}>
          {t('cancel')}
        </Button>

        <div className="space-x-2">
          {step !== 'org' && (
            <Button onClick={onPrevStep} variant="outlined">
              {t('previous')}
            </Button>
          )}
          {step === 'profiles' ? (
            <Button type="submit" onClick={onSave}>
              {action}
            </Button>
          ) : step !== 'org' ? (
            <Button onClick={onNextStep}>{t('next')}</Button>
          ) : null}
        </div>
      </DrawerFooter>
    </Drawer>
  );
}

function OrgStep({
  onSelectOrg,
  existingOrgAxleIds,
}: {
  onSelectOrg: (axleOrgId: string) => void;
  existingOrgAxleIds: string[];
}) {
  const { t } = useTranslation();
  const { toast } = useToast();
  const [query, setQuery] = useState('');
  const [queryInput, setQueryInput] = useState('');
  const limit = PAGE_LIMIT;
  const [cursor, setCursor] = useState<string>('');

  const { data, error } = trpc.admin.axleOrgs.useQuery({
    limit,
    query,
    cursor,
  });

  return (
    <DrawerBodyWrapper
      message={t('selectAnOrgToImport')}
      search={
        <>
          <TextField
            placeholder={t('searchOrgs')}
            className="flex-grow"
            value={queryInput}
            onChange={(e) => setQueryInput(e.target.value)}
            onKeyDown={(e) => {
              if (e.key === 'Enter') {
                setQuery(queryInput);
                setCursor('');
              }
            }}
          />
          <Button
            onClick={() => {
              setQuery(queryInput);
              setCursor('');
            }}
          >
            Search
          </Button>
          <Pagination
            cursor={data?.cursor || { previousPage: '', nextPage: '' }}
            setCursor={setCursor}
            resultsCount={data?.res.length}
          />
        </>
      }
      error={!!error}
      listItems={data?.res?.map((org) => (
        <AdminListItem
          disabled={existingOrgAxleIds.some((id) => id === org.axleOrgId)}
          key={org.axleOrgId}
          onClick={() => {
            // Fail fast: the Hub Org Document must carry an
            // athleteUserTypeDocumentId before we let the admin proceed,
            // otherwise the import blows up several steps later when we
            // fetch the athlete user type.
            if (!org.athleteUserTypeDocumentId) {
              // Admin-only page: message is intentionally English-only, matching
              // the other hardcoded admin toasts.
              toast({
                title:
                  'This org does not have the required athleteUserTypeDocumentId, please resync this org to Axle from GA2 and check that this org is an Athletic org. You can verify the existence of athleteUserTypeDocumentId in the Hub Org Document through Axle.',
                severity: 'error',
                duration: 10000,
              });
              return;
            }
            onSelectOrg(org.axleOrgId);
          }}
          selected={false}
          label={org.name}
        />
      ))}
      itemsSubLabel={`${data?.res.length ?? 0} orgs`}
    />
  );
}

function TeamsStep({
  axleOrgId,
  editOrg,
  selectedTeamIds,
  onAddTeam,
  onRemoveTeam,
}: {
  axleOrgId: string;
  selectedTeamIds: string[];
  onAddTeam: (axleTeamId: string) => void;
  onRemoveTeam: (axleTeamId: string) => void;
  editOrg?: Org;
}) {
  const { t } = useTranslation();
  const { data, error } = trpc.admin.axleTeams.useQuery({
    axleOrgId,
  });

  return (
    <DrawerBodyWrapper
      message={t('selectTeamsToImport')}
      itemsSubLabel={`${selectedTeamIds.length} teams selected`}
      search={null}
      listItems={data?.map((team) => {
        const selected = selectedTeamIds.includes(team.axleTeamId);
        return (
          <AdminListItem
            disabled={editOrg?.teams.some(
              (t) => t.axleTeamId === team.axleTeamId,
            )}
            key={team.axleTeamId}
            onClick={() => {
              if (selected) {
                onRemoveTeam(team.axleTeamId);
              } else {
                onAddTeam(team.axleTeamId);
              }
            }}
            selected={selected}
            label={team.name}
          />
        );
      })}
      error={!!error}
    />
  );
}

function ProfilesStep({
  axleOrgId,
  axleTeamIds,
  selectedProfileIds,
  onAddProfile,
  onRemoveProfile,
}: {
  axleOrgId: string;
  axleTeamIds: string[];
  selectedProfileIds: string[];
  onAddProfile: (axleProfileId: string) => void;
  onRemoveProfile: (axleProfileId: string) => void;
}) {
  const { t } = useTranslation();
  const [query, setQuery] = useState('');
  const [queryInput, setQueryInput] = useState('');
  const [cursor, setCursor] = useState<string>('');
  const limit = PAGE_LIMIT;

  const { data, error } = trpc.admin.coachAxleProfiles.useQuery({
    axleOrgId,
    axleTeamIds,
    query,
    limit,
    cursor,
  });

  return (
    <DrawerBodyWrapper
      message={t('selectProfilesForLoginAccess')}
      itemsSubLabel={`${selectedProfileIds.length} profiles selected`}
      search={
        <>
          <TextField
            placeholder={t('searchByLastName')}
            className="flex-grow"
            value={queryInput}
            onChange={(e) => setQueryInput(e.target.value)}
            onKeyDown={(e) => {
              if (e.key === 'Enter') {
                setQuery(queryInput);
                setCursor('');
              }
            }}
          />
          <Button
            onClick={() => {
              setQuery(queryInput);
              setCursor('');
            }}
          >
            Search
          </Button>
          <Pagination
            cursor={data?.cursor || { previousPage: '', nextPage: '' }}
            setCursor={setCursor}
            resultsCount={data?.res.length}
          />
        </>
      }
      error={!!error}
      listItems={data?.res
        .sort((a, b) => a.familyName.localeCompare(b.familyName))
        .map((profile) => {
          const selected = selectedProfileIds.includes(profile.axleProfileId);
          return (
            <AdminListItem
              key={profile.axleProfileId}
              onClick={() => {
                if (selected) {
                  onRemoveProfile(profile.axleProfileId);
                } else {
                  onAddProfile(profile.axleProfileId);
                }
              }}
              selected={selected}
              label={`${profile.familyName}, ${profile.givenName}`}
            />
          );
        })}
    />
  );
}

const DrawerBodyWrapper = ({
  message,
  search,
  listItems,
  error,
  itemsSubLabel,
}: {
  message: string;
  search: React.ReactNode;
  listItems?: React.ReactNode;
  error: boolean;
  itemsSubLabel: string;
}) => {
  const { t } = useTranslation();

  return (
    <>
      <Typography variant="body2Medium" fontWeight={'bold'}>
        {message}
      </Typography>
      <div className="flex items-end gap-1 pb-2 pt-2">{search}</div>
      <div className="border px-3 py-1 mt-0 my-2 border-gray-200 rounded-sm h-[calc(100vh-400px)] overflow-y-scroll">
        {listItems ? (
          <List
            sx={{
              '& .MuiTypography-root': {
                fontSize: '14px',
              },
              '& .MuiListItemButton-root': {
                mb: 1,
                py: 0.5,
                '&:hover': {
                  borderRadius: 1,
                  backgroundColor: grey[200],
                },
              },
              '& .Mui-selected': {
                borderRadius: 1,
                backgroundColor: grey[200],
              },
            }}
          >
            {listItems}
          </List>
        ) : error ? (
          <Typography>{t('somethingWentWrong')}</Typography>
        ) : (
          <LoadingItems />
        )}
      </div>
      <Typography variant="body2">{itemsSubLabel}</Typography>
    </>
  );
};

const AdminListItem = ({
  disabled,
  onClick,
  selected,
  label,
}: {
  disabled?: boolean;
  label: string;
  selected: boolean;
  onClick: () => void;
}) => {
  return (
    <ListItemButton disabled={disabled} onClick={onClick} selected={selected}>
      <ListItemText primary={label} />
      {selected && <MdCancel size={20} />}
    </ListItemButton>
  );
};

function Pagination({
  cursor,
  resultsCount,
  setCursor,
}: {
  resultsCount: number | undefined;
  cursor: Cursor | undefined;
  setCursor: (cursor: string) => void;
}) {
  const previousPage = cursor?.previousPage ?? '';
  const nextPage = cursor?.nextPage ?? '';
  return (
    <>
      <Button
        disabled={previousPage === ''}
        onClick={() => setCursor(previousPage)}
      >
        <MdOutlineChevronLeft size={20} />
      </Button>
      <Button
        // Only nextPage being empty means the stream is over: the server may
        // return fewer than `limit` results mid-stream (it walks Axle pages
        // until it has `limit` coach-qualified profiles or the stream ends),
        // so a short result must not end the walk.
        disabled={resultsCount === undefined || nextPage === ''}
        onClick={() => setCursor(nextPage)}
      >
        <MdOutlineChevronRight size={20} />
      </Button>
    </>
  );
}

function LoadingItems() {
  return (
    <>
      <Skeleton variant="rectangular" height={48} className="mb-1" />
      <Skeleton variant="rectangular" height={48} className="mb-1" />
      <Skeleton variant="rectangular" height={48} className="mb-1" />
    </>
  );
}
